import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { motion } from 'framer-motion';
import { CalendarBlank, Clock, MapPin, Sparkle, ArrowRight } from '@phosphor-icons/react';

/**
 * AppointmentPrepCard — Phase 4
 *
 * Rendered on /home when the user has an upcoming appointment within 3 days.
 * Loads the prep report in the background (or generates it) and surfaces the
 * top 2–3 likely-discussion-points as a visible preview, with a tactile CTA
 * "Open full prep" linking to /appointments/:id/prep.
 *
 * Fires `prep_card_viewed` once per mount.
 */
export const AppointmentPrepCard = () => {
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(true);

  // Find the soonest upcoming appointment within 3 days
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/appointments/upcoming`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.appointments || [];
        const now = new Date();
        const candidate = list
          .filter((a) => a.status === 'upcoming' && a.date)
          .map((a) => ({ ...a, _dt: new Date(`${a.date}T${a.time || '00:00'}`) }))
          .filter((a) => {
            const diff = (a._dt - now) / 86400000;
            return diff >= 0 && diff <= 3.5;
          })
          .sort((a, b) => a._dt - b._dt)[0];
        if (candidate) {
          setAppt(candidate);
          try {
            const { data: pr } = await axios.get(`${API}/appointments/${candidate.id}/prep-report`);
            if (!cancelled) setPrep(pr);
          } catch {
            /* prep not yet generated — show the shell, user can click through */
          }
        }
      } catch (err) {
        console.debug('[AppointmentPrepCard] fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fire analytics once we have an appt to show
  useEffect(() => {
    if (appt?.id) {
      axios.post(`${API}/activation/track`, {
        event: 'prep_card_viewed',
        props: { appointment_id: appt.id, source: 'home_card', has_prep: !!prep },
      }).catch(() => {});
    }
  }, [appt?.id, prep]);

  const daysUntil = useMemo(() => {
    if (!appt?.date) return null;
    const now = new Date();
    const then = new Date(`${appt.date}T${appt.time || '00:00'}`);
    const diff = Math.round((then - now) / 86400000);
    if (diff <= 0) return 'today';
    if (diff === 1) return 'tomorrow';
    return `in ${diff} days`;
  }, [appt]);

  const topics = useMemo(() => {
    if (!prep?.ai_insights) return [];
    const out = [];
    for (const x of (prep.ai_insights.key_concerns || []).slice(0, 2)) if (x) out.push(x);
    for (const x of (prep.ai_insights.suggested_questions || []).slice(0, 2)) {
      if (x && out.length < 3) out.push(x);
    }
    return out;
  }, [prep]);

  if (loading || !appt) return null;

  const goPrep = () => navigate(`/appointments/${appt.id}/prep`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-6"
      data-testid="home-appointment-prep-card"
    >
      <div
        className="rounded-2xl overflow-hidden border border-[#DFD9CF] bg-[#FBFAF6] cursor-pointer hover:border-[#CFC8BB] transition-colors"
        onClick={goPrep}
        role="link"
      >
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-[#2FB3A0]/10 flex items-center justify-center">
                <Sparkle size={13} weight="fill" className="text-[#2FB3A0]" />
              </div>
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em]">
                Appointment prep &middot; {daysUntil}
              </span>
            </div>
          </div>

          <h3 className="mt-3 text-[18px] font-semibold text-[#0E1C2E] leading-snug tracking-[-0.01em]" data-testid="prep-card-title">
            {appt.title}
          </h3>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <CalendarBlank size={13} /> {appt.date}
            </span>
            {appt.time && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={13} /> {appt.time}
              </span>
            )}
            {(appt.clinician || appt.clinic) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={13} /> {appt.clinician || appt.clinic}
              </span>
            )}
          </div>

          {topics.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="prep-card-topics">
              {topics.map((t, i) => (
                <div key={i} className="flex items-start gap-2.5 text-[14px] text-slate-700 leading-relaxed">
                  <span className="mt-[9px] inline-block h-[2px] w-[10px] bg-[#2FB3A0] flex-shrink-0 rounded" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#ECE8E1] bg-white flex items-center justify-between">
          <span className="text-[13px] text-slate-500">
            {prep ? 'Kindra has a quiet brief ready.' : 'Generate a prep brief from your history.'}
          </span>
          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#0E1C2E]" data-testid="prep-card-cta">
            Open full prep <ArrowRight size={14} weight="bold" />
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default AppointmentPrepCard;
